import React, { useState, useEffect } from 'react';
import { collection, getDocs, getDoc, query, where, orderBy, doc, limit, updateDoc, serverTimestamp } from 'firebase/firestore';
import { onSnapshot } from '../utils/firestoreListen';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ActivityWallModal from './ActivityWallModal';
import { dailyActivityService } from '../services/dailyActivityService';
import { resolveImageUrl } from '../utils/urlResolver';
import { getCache, setCache, createCacheKey, clearCache } from '../utils/cache';
import { trackEvent, logError } from '../utils/observability';
import { coLearningInkBody, coLearningInkTitle } from '../ui/coLearningModalTheme';
import CoLearningPromptBackdrop from '../ui/CoLearningPromptBackdrop';
import { useArtistSalonAgeFilter } from '../context/ArtistSalonAgeFilterContext';

const dailySnapshotKey = (uid) => `co-learning:daily:${uid || 'anon'}`;

const DailyActivitiesShowcase = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { ageFilter, filterSubmissions } = useArtistSalonAgeFilter();
  const [activities, setActivities] = useState(() => {
    try {
      const raw = sessionStorage.getItem(dailySnapshotKey(user?.uid));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed?.activities) ? parsed.activities : [];
    } catch (_) {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  // Prevent flicker: only show skeletons during the very first load
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [publicSubmissions, setPublicSubmissions] = useState(() => {
    try {
      const raw = sessionStorage.getItem(dailySnapshotKey(user?.uid));
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed?.publicSubmissions || {};
    } catch (_) {
      return {};
    }
  });
  const [globalPublicByActivity, setGlobalPublicByActivity] = useState({}); // { [activityId]: [{id, imageUrl, createdAt}] }
  const [publicStats, setPublicStats] = useState(() => {
    try {
      const raw = sessionStorage.getItem(dailySnapshotKey(user?.uid));
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed?.publicStats || {};
    } catch (_) {
      return {};
    }
  }); // { [activityId]: { publicCount, previewThumbUrl, previewSubmissionId, previewArtistName, previewUpdatedAt } }
  const [userParticipations, setUserParticipations] = useState(new Set());
  const [shareStatusByActivity, setShareStatusByActivity] = useState({}); // { [activityId]: { hasPublic: boolean, latestSubmissionId: string|null } }
  const [sharePrompt, setSharePrompt] = useState(null); // { activityId, latestSubmissionId }
  const [wallModal, setWallModal] = useState(null); // activityId
  const [myShared, setMyShared] = useState([]);
  const [userSubmissionsByActivity, setUserSubmissionsByActivity] = useState({}); // { [activityId]: Submission[] }
  const [visibleDailyActivities, setVisibleDailyActivities] = useState(() => {
    try {
      const raw = sessionStorage.getItem(dailySnapshotKey(user?.uid));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed?.visibleDailyActivities) ? parsed.visibleDailyActivities : [];
    } catch (_) {
      return [];
    }
  });
  const [publicPreviewByActivity, setPublicPreviewByActivity] = useState({}); // { [activityId]: imageUrl }
  const [selectedSharedArtwork, setSelectedSharedArtwork] = useState(null);
  const [showLockedMessage, setShowLockedMessage] = useState(null); // activityId for which to show locked message

  useEffect(() => {
    try {
      if (!user?.uid) return;
      const raw = sessionStorage.getItem(dailySnapshotKey(user.uid));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.activities) && parsed.activities.length > 0) {
        setActivities(parsed.activities);
      }
      if (parsed?.publicSubmissions && typeof parsed.publicSubmissions === 'object') {
        setPublicSubmissions(parsed.publicSubmissions);
      }
      if (parsed?.publicStats && typeof parsed.publicStats === 'object') {
        setPublicStats(parsed.publicStats);
      }
      if (Array.isArray(parsed?.visibleDailyActivities) && parsed.visibleDailyActivities.length > 0) {
        setVisibleDailyActivities(parsed.visibleDailyActivities);
      }
    } catch (_) {}
  }, [user?.uid]);

  useEffect(() => {
    try {
      if (!user?.uid) return;
      if (!activities?.length && !visibleDailyActivities?.length) return;
      sessionStorage.setItem(
        dailySnapshotKey(user.uid),
        JSON.stringify({
          activities,
          publicSubmissions,
          publicStats,
          visibleDailyActivities
        })
      );
      const previewUrls = [
        ...Object.values(publicStats || {})
          .map((s) => s?.previewThumbUrl)
          .filter(Boolean),
        ...Object.values(publicSubmissions || {})
          .flatMap((arr) => (Array.isArray(arr) ? arr.slice(0, 1) : []))
          .map((s) => s?.imageUrl || s?.userArtworkUrl)
          .filter(Boolean)
      ].slice(0, 24);
      previewUrls.forEach((src) => {
        try {
          const img = new Image();
          img.decoding = 'async';
          img.src = resolveImageUrl(src);
        } catch (_) {}
      });
    } catch (_) {}
  }, [user?.uid, activities, publicSubmissions, publicStats, visibleDailyActivities]);

  // Effect to prevent modal from opening if user hasn't submitted/shared
  useEffect(() => {
    if (wallModal) {
      const aid = wallModal;
      const hasParticipated = userParticipations instanceof Set ? userParticipations.has(aid) : false;
      const userSubs = userSubmissionsByActivity[aid] || [];
      const hasSubmitted = hasParticipated || userSubs.length > 0;
      const shareStatus = shareStatusByActivity[aid];
      const hasPublicSubmission = shareStatus?.hasPublic || userSubs.some(sub => sub.isPublic === true);
      const isUnlocked = !!hasPublicSubmission;
      
      // If user hasn't submitted or hasn't shared, close modal and show pop-up instead
      if (!hasSubmitted || !isUnlocked) {
        setShowLockedMessage(aid);
        setWallModal(null);
      }
    }
  }, [wallModal, userParticipations, userSubmissionsByActivity, shareStatusByActivity]);

  // Derived: any overlay modal currently open
  const isOverlayOpen = !!(selectedActivity || sharePrompt || wallModal || selectedSharedArtwork || showLockedMessage);

  useEffect(() => {
    try { console.log('DailyActivitiesShowcase layout v2 loaded'); } catch (_) {}
    if (user) {
      loadAllActivities();
      loadUserParticipations();
      loadPublicStats();
      loadMyShared();
      let unsubscribe = null;
      // Kick off initial load + real-time listener for the user's submissions.
      // We capture the unsubscribe function once the async helper resolves.
      (async () => {
        try {
          const maybeUnsub = await loadUserSubmissions();
          if (typeof maybeUnsub === 'function') {
            unsubscribe = maybeUnsub;
          }
        } catch (err) {
          console.error('Error setting up user submissions listener:', err);
        }
      })();
      // Always return a cleanup function
      return () => {
        if (unsubscribe && typeof unsubscribe === 'function') {
          try {
            unsubscribe();
          } catch (e) {
            console.error('Error during user submissions listener cleanup:', e);
          }
        }
      };
    }
    // CRITICAL: Always return a cleanup function
    return () => {};
  }, [user]);

  // When activities list loads, fetch public submissions for ALL activities
  useEffect(() => {
    if (activities && activities.length > 0) {
      const ids = activities.map(a => a.id);
      // CRITICAL: Don't return the Promise from async function
      loadPublicSubmissions(ids).catch(err => {
        console.error('[DAILY] Error loading public submissions:', err);
      });
    }
    // CRITICAL: Always return a cleanup function
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities?.length]);

  // NOTE: Previous versions used per-activity real-time listeners on `submissions`
  // to keep the grid in sync. This caused O(A) active listeners across many
  // activities. We now rely on one-time reads (`loadPublicSubmissions`,
  // `loadPublicStats`) and let ActivityWallModal handle real-time updates
  // for a specific activity wall.

  // Recompute which activities should be shown in the grid (any public shares)
  useEffect(() => {
    if (!activities) {
      // CRITICAL: Always return a cleanup function
      return () => {};
    }
    // Build a set of activity ids that have public content (from submissions or stats)
    const idSet = new Set();
    Object.keys(publicSubmissions || {}).forEach((k) => {
      const arr = filterSubmissions(publicSubmissions[k] || []);
      if (arr.length > 0) idSet.add(k.toString());
    });
    if (ageFilter === 'all') {
      Object.keys(publicStats || {}).forEach((k) => {
        const n = Number(publicStats[k]?.publicCount || 0);
        if (n > 0) idSet.add(k.toString());
      });
    }

    const byId = new Map((activities || []).map(a => [a.id.toString(), a]));
    const visible = Array.from(idSet).map((id) => {
      const a = byId.get(id);
      return a || { id, title: `Daily Activity ${id}`, description: '' };
    });
    try { console.log('Visible daily activities:', visible.map(v => v.id)); } catch (_) {}
    setVisibleDailyActivities(visible);
    // CRITICAL: Always return a cleanup function
    return () => {};
  }, [activities, publicSubmissions, publicStats, ageFilter, filterSubmissions]);

  // Ensure we have at least one preview image for any visible activity with a public count
  useEffect(() => {
    // Preview images are now provided via publicDailyActivityStats (previewThumbUrl),
    // so no per-activity preview queries are required here.
    return () => {};
  }, [visibleDailyActivities, publicStats]);
  const loadMyShared = async () => {
    try {
      const items = await dailyActivityService.getUserSharedSubmissions(user.uid);
      setMyShared(items);
    } catch (_) {
      setMyShared([]);
    }
  };

  // Fetch ALL of the current user's submissions across daily activities
  // Includes both locked (private) and unlocked (public) submissions
  const loadUserSubmissions = async () => {
    if (!user) return;
    try {
      const q = query(
        collection(db, 'submissions'),
        where('uid', '==', user.uid)
      );
      const snap = await getDocs(q);
      const grouped = {};
      snap.docs.forEach((d) => {
        const data = d.data();
        const aid = (data.activityId || '').toString();
        if (!aid) return;
        if (!grouped[aid]) grouped[aid] = [];
        grouped[aid].push({ id: d.id, ...data });
      });
      // Sort each activity's submissions newest first (by createdAt if present)
      Object.keys(grouped).forEach((aid) => {
        grouped[aid].sort((a, b) => {
          const at = a.createdAt?.toMillis?.() || 0;
          const bt = b.createdAt?.toMillis?.() || 0;
          return bt - at;
        });
      });
      setUserSubmissionsByActivity(grouped);
      
      // Set up real-time listener for user's submissions to catch share/unshare changes immediately
      const unsubscribe = onSnapshot(q, (snap) => {
        const updatedGrouped = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          const aid = (data.activityId || '').toString();
          if (!aid) return;
          if (!updatedGrouped[aid]) updatedGrouped[aid] = [];
          updatedGrouped[aid].push({ id: d.id, ...data });
        });
        // Sort each activity's submissions newest first
        Object.keys(updatedGrouped).forEach((aid) => {
          updatedGrouped[aid].sort((a, b) => {
            const at = a.createdAt?.toMillis?.() || 0;
            const bt = b.createdAt?.toMillis?.() || 0;
            return bt - at; // desc
          });
        });
        setUserSubmissionsByActivity(updatedGrouped);
        console.log('🔄 [DAILY] User submissions updated in real-time');
      }, (error) => {
        console.error('Error in user submissions real-time listener:', error);
      }, { purpose: 'DailyActivitiesShowcase-user-submissions', component: 'DailyActivitiesShowcase' });
      
      // Return cleanup function for the listener
      return unsubscribe;
    } catch (error) {
      console.error('Error loading user submissions by activity:', error);
      setUserSubmissionsByActivity({});
      return () => {}; // Return empty cleanup function on error
    }
  };

  // Convert local URLs to full backend URLs
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl || typeof imageUrl !== 'string') {
      return '';
    }
    
    // Use centralized URL resolver to handle localhost:3001 URLs
    return resolveImageUrl(imageUrl);
  };

  // Lock body scroll and add black background while any overlay is open
  useEffect(() => {
    const previous = document.body.style.overflow;
    const previousBg = document.body.style.backgroundColor;
    if (isOverlayOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.backgroundColor = 'black';
    } else {
      document.body.style.overflow = previous || '';
      document.body.style.backgroundColor = previousBg || '';
    }
    return () => {
      document.body.style.overflow = previous || '';
      document.body.style.backgroundColor = previousBg || '';
    };
  }, [isOverlayOpen]);

  // When participations load, compute share status and optionally load public submissions
  useEffect(() => {
    if (!user) return () => {}; // Always return a cleanup function
    
    const ids = userParticipations instanceof Set ? Array.from(userParticipations) : [];
    if (ids.length > 0) {
      loadUserShareStatus(ids);
      // Load public submissions (async, but don't return its Promise as cleanup)
      loadPublicSubmissions(ids).catch(err => {
        console.error('[DAILY] Error loading public submissions:', err);
      });
    } else {
      setShareStatusByActivity({});
      setPublicSubmissions([]);
    }
    
    // CRITICAL: Always return a cleanup function, never a Promise
    return () => {
      // No cleanup needed for one-time data fetch
    };
  }, [user, userParticipations]);

  const loadAllActivities = async () => {
    try {
      if (!hasLoadedOnce) setLoading(true);
      
      // Check cache first - but always apply title correction
      const cacheKey = createCacheKey('activities', { type: 'daily_activity' });
      const cached = getCache(cacheKey);
      if (cached) {
        console.log('✅ [DAILY] Using cached activities:', cached.length);
        // Apply title correction to cached activities as well
        const correctedCached = cached.map(activity => {
          // Use the ID as-is (it should already be correct from number field)
          const id = activity.id || activity.number;
          // Always override title to match the ID
          const correctedTitle = `Daily Activity ${id}`;
          return {
            ...activity,
            id,
            title: correctedTitle // Ensure title matches ID
          };
        });
        setActivities(correctedCached);
        setLoading(false);
        if (!hasLoadedOnce) setHasLoadedOnce(true);
        return;
      }
      
      const results = [];
      // Load all daily activities
      const qActs = query(collection(db, 'activities'), where('type', '==', 'daily_activity'));
      const snap = await getDocs(qActs);
      snap.docs.forEach(aDoc => {
        const data = aDoc.data();
        // FIX: Use number field as the primary source of truth for ID
        // The number field should match the actual activity number (1, 2, 3, etc.)
        // Even if the title is wrong, we trust the number field
        let id = data.number;
        
        // Only extract from title if number field is missing or invalid
        if (!id || Number.isNaN(Number(id))) {
          const titleMatch = data.title?.match(/Daily Activity (\d+)/i);
          if (titleMatch && titleMatch[1]) {
            const extractedNumber = Number(titleMatch[1]);
            if (!Number.isNaN(extractedNumber)) {
              id = extractedNumber;
              console.log(`🔧 [DAILY] Using title-extracted ID: ${extractedNumber} from title "${data.title}" (number field was ${data.number})`);
            }
          }
        }
        
        // Fallback to doc id if no number found
        if (!id) id = aDoc.id;
        
        // FIX: Always override title to match the correct ID (from number field)
        // This ensures Activity 1 shows as "Daily Activity 1" even if Firestore title says "Daily Activity 33"
        const correctedTitle = `Daily Activity ${id}`;
        const correctedData = {
          ...data,
          title: correctedTitle, // Override with correct title based on ID from number field
          originalTitle: data.title // Keep original for reference/debugging
        };
        
        if (data.title !== correctedTitle) {
          console.log(`🔧 [DAILY] Corrected title: "${data.title}" -> "${correctedTitle}" (ID: ${id}, number field: ${data.number})`);
        }
        
        results.push({ id, ...correctedData });
      });
      // Sort by numeric id if possible
      results.sort((a, b) => {
        const ai = Number(a.id);
        const bi = Number(b.id);
        if (Number.isNaN(ai) || Number.isNaN(bi)) return String(a.id).localeCompare(String(b.id));
        return ai - bi;
      });
      
      // Cache the results (10 min cache - activities don't change often)
      setCache(cacheKey, results, 10 * 60 * 1000);
      console.log('✅ [DAILY] Activities cached');
      
      setActivities(results);
    } catch (error) {
      console.error('Error loading all daily activities:', error);
      setActivities([]);
    } finally {
      setLoading(false);
      if (!hasLoadedOnce) setHasLoadedOnce(true);
    }
  };

  const loadUserParticipations = async () => {
    if (!user) return;

    // Check cache first
    const cacheKey = createCacheKey('activityParticipants', { uid: user.uid }, user.uid);
    const cached = getCache(cacheKey);
    if (cached !== null && cached !== undefined) {
      try {
        // Convert cached array back to Set (Sets don't serialize well, so we cache as arrays)
        // Handle both array format (new) and Set format (old, which becomes empty object)
        let participationSet;
        if (Array.isArray(cached)) {
          participationSet = new Set(cached);
        } else if (cached && typeof cached === 'object' && Object.keys(cached).length === 0) {
          // Empty object means it was a Set that got serialized incorrectly - treat as empty
          participationSet = new Set();
        } else {
          // Fallback: try to convert to Set, or create empty Set
          participationSet = new Set();
        }
        console.log('✅ [DAILY] Using cached participations:', participationSet.size);
        setUserParticipations(participationSet);
        return;
      } catch (error) {
        console.error('Error parsing cached participations:', error);
        // Clear bad cache and continue to fetch fresh data
        clearCache(cacheKey);
      }
    }

    try {
      const participationsQuery = query(
        collection(db, 'activityParticipants'),
        where('uid', '==', user.uid)
      );
      
      const snapshot = await getDocs(participationsQuery);
      const participationSet = new Set();
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        participationSet.add(data.activityId);
      });
      
      // Cache as array (Sets don't serialize well with JSON.stringify)
      const participationArray = Array.from(participationSet);
      setCache(cacheKey, participationArray, 5 * 60 * 1000);
      console.log('✅ [DAILY] Participations cached');
      
      setUserParticipations(participationSet);
    } catch (error) {
      console.error('Error loading user participations:', error);
    }
  };

  const loadPublicSubmissions = async (activityIds = []) => {
    try {
      const submissionsByActivity = {};
      // Query per-activity to respect security rules gate
      for (const id of activityIds) {
        let arr = [];
        // Try as string id
        try {
          const qStr = query(
          collection(db, 'submissions'),
          where('activityId', '==', id.toString()),
          where('isPublic', '==', true)
        );
          const snapStr = await getDocs(qStr);
          arr = snapStr.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (_) {}

        // If no results, try as numeric id (some documents may store activityId as number)
        if (!arr || arr.length === 0) {
          const asNum = Number(id);
          if (!Number.isNaN(asNum)) {
            try {
              const qNum = query(
                collection(db, 'submissions'),
                where('activityId', '==', asNum),
                where('isPublic', '==', true)
              );
              const snapNum = await getDocs(qNum);
              arr = snapNum.docs.map(d => ({ id: d.id, ...d.data() }));
            } catch (_) {}
          }
        }
        arr.sort((a, b) => {
          const at = a.createdAt?.toMillis?.() || 0;
          const bt = b.createdAt?.toMillis?.() || 0;
          return bt - at; // desc
        });
        submissionsByActivity[id] = arr;
      }
      setPublicSubmissions(prev => ({ ...prev, ...submissionsByActivity }));
    } catch (error) {
      console.error('Error loading public submissions:', error);
    }
  };

  // Load safe public stats (counts only) to decide visibility of cards
  const loadPublicStats = async () => {
    // Check cache first
    const cacheKey = createCacheKey('publicDailyActivityStats');
    const cached = getCache(cacheKey);
    if (cached) {
      console.log('✅ [DAILY] Using cached public stats');
      setPublicStats(cached);
      return;
    }
    
    try {
      const map = {};
      const snap = await getDocs(collection(db, 'publicDailyActivityStats'));
      snap.docs.forEach(d => {
        const data = d.data();
        const aid = (data.activityId || d.id || '').toString();
        if (!aid) return;
        map[aid] = {
          publicCount: Number(data.publicCount || 0),
          previewThumbUrl: data.previewThumbUrl || '',
          previewSubmissionId: data.previewSubmissionId || '',
          previewArtistName: data.previewArtistName || '',
          previewUpdatedAt: data.previewUpdatedAt || null
        };
      });
      
      // Cache the results (3 min cache - stats update frequently)
      setCache(cacheKey, map, 3 * 60 * 1000);
      console.log('✅ [DAILY] Public stats cached');
      
      setPublicStats(map);
    } catch (e) {
      console.error('Error loading public stats:', e);
      setPublicStats({});
    }
  };

  // Determine if the user has a public submission per activity and track latest submission id
  const loadUserShareStatus = async (activityIds) => {
    try {
      const status = {};
      for (const id of activityIds) {
        // Avoid composite index by omitting orderBy; pick latest on client
        const qUser = query(
          collection(db, 'submissions'),
          where('activityId', '==', id.toString()),
          where('uid', '==', user.uid)
        );
        const snap = await getDocs(qUser);
        if (!snap.empty) {
          // Pick the most recent by createdAt if present, else by doc id
          const docs = snap.docs;
          const latest = docs.reduce((best, cur) => {
            const b = best?.data();
            const c = cur.data();
            const bt = b?.createdAt?.toMillis?.() || 0;
            const ct = c?.createdAt?.toMillis?.() || 0;
            if (ct !== bt) return ct > bt ? cur : best;
            return cur.id > (best?.id || '') ? cur : best;
          }, null);
          const data = latest.data();
          status[id] = { hasPublic: !!data.isPublic, latestSubmissionId: latest.id };
        } else {
          status[id] = { hasPublic: false, latestSubmissionId: null };
        }
      }
      setShareStatusByActivity(status);
    } catch (e) {
      console.error('Error loading user share status:', e);
    }
  };

  const handleActivityClick = (activity) => {
    // Open the ActivityWallModal with activity details on left and submissions on right
    setWallModal(activity.id);
  };

  const confirmShareNow = async () => {
    if (!sharePrompt || !sharePrompt.latestSubmissionId) {
      setSharePrompt(null);
      return;
    }
    try {
      await updateDoc(doc(db, 'submissions', sharePrompt.latestSubmissionId), { isPublic: true, updatedAt: serverTimestamp() });
      trackEvent('daily_submission_published', {
        submissionId: sharePrompt.latestSubmissionId,
        activityId: sharePrompt.activityId,
        userId: user?.uid || null,
      });
      // Refresh status and navigate
      await loadUserShareStatus([sharePrompt.activityId]);
      const targetId = sharePrompt.activityId;
      setSharePrompt(null);
      setWallModal(targetId);
    } catch (e) {
      console.error('Error sharing submission now:', e);
      setSharePrompt(null);
      logError(e, {
        where: 'DailyActivitiesShowcase.confirmShareNow',
        activityId: sharePrompt?.activityId,
        userId: user?.uid || null,
      });
    }
  };

  if (loading && !hasLoadedOnce) {
    return (
      <div className="min-h-[520px] flex flex-col items-center justify-center py-16 px-4">
        <div
          className="animate-spin rounded-full h-14 w-14 border-t-2 border-amber-500 border-r-2 border-amber-300/60 border-b-2 border-amber-200/40"
          aria-hidden="true"
        />
        <p className="mt-5 text-sm font-semibold text-[#2F251B]">
          Opening the Daily Art Studio gallery…
        </p>
        <p className="mt-1 text-xs text-[#8A6A44]">Gathering exhibits and shared artworks.</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isOverlayOpen ? 'overflow-hidden' : ''}`}>
      {/* Description text removed - now shown in parent GalleryStudio component above */}

      {/* REMOVED: Duplicate grid - using visibleDailyActivities grid below instead to avoid showing same activity twice */}

      {/* Daily Activities Walls (includes locked if user hasn't shared) */}
      <div className="mt-4 md:mt-6">
        <div
          className={`grid gap-6 md:gap-8 ${
            visibleDailyActivities.length === 1
              ? 'lg:grid-cols-[minmax(0,420px)_minmax(260px,1fr)] lg:items-start lg:justify-center'
              : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
          }`}
        >
          {visibleDailyActivities.map((activity) => {
              const aid = activity.id;
              const hasParticipated = userParticipations instanceof Set ? userParticipations.has(aid) : false;
              // Check if user has submitted artwork (regardless of public/private)
              const userSubs = userSubmissionsByActivity[aid] || [];
              const hasSubmitted = hasParticipated || userSubs.length > 0;
              // Check if user has shared publicly - check both shareStatusByActivity and userSubmissionsByActivity
              const shareStatus = shareStatusByActivity[aid];
              const hasPublicSubmission = shareStatus?.hasPublic || userSubs.some(sub => sub.isPublic === true);
              const isUnlocked = !!hasPublicSubmission;
              const stats = publicStats[aid] || {};
              const filteredPublicSubs = filterSubmissions(publicSubmissions?.[aid] || []);
              const liveCount = filteredPublicSubs.length;
              const count =
                ageFilter === 'all'
                  ? typeof publicSubmissions?.[aid]?.length === 'number'
                    ? publicSubmissions[aid].length
                    : Number(stats.publicCount || 0)
                  : liveCount;
              const bannerUrl =
                stats.previewThumbUrl ||
                filteredPublicSubs[0]?.imageUrl ||
                filteredPublicSubs[0]?.userArtworkUrl ||
                '';

              return (
                <div
                  key={aid}
                  className="group relative mx-auto w-full max-w-[420px] cursor-pointer rounded-[28px] border border-[#F6D57B]/90 bg-gradient-to-b from-[#FFFDF9] to-[#FFF5E8] p-5 shadow-[0_16px_44px_rgba(120,72,20,0.1)] transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_22px_52px_rgba(120,72,20,0.14)] sm:p-6 lg:mx-0"
                  data-testid="daily-activity-card"
                  onClick={() => {
                    // If user hasn't submitted, show pop-up message
                    if (!hasSubmitted) {
                      setShowLockedMessage(aid);
                      return;
                    }
                    // If user has submitted but hasn't shared, show pop-up message
                    if (!isUnlocked) {
                      setShowLockedMessage(aid);
                      return;
                    }
                    // If unlocked, open the ActivityWallModal
                    setWallModal(aid);
                  }}
                >
                  {/* Header row */}
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#F5D99B] bg-gradient-to-br from-[#F59E0B] to-[#F97316] text-sm font-bold text-white shadow-sm">
                        {aid}
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-lg font-semibold leading-snug text-[#2F251B] sm:text-[1.35rem]">{activity.title}</h4>
                        <p className="mt-1 text-xs text-[#8A6A44] sm:text-sm">See how young artists responded to this activity.</p>
                      </div>
                    </div>
                    {hasSubmitted && isUnlocked ? (
                      <div className="shrink-0 text-emerald-600" title="Unlocked: You have shared your artwork">
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div className="shrink-0 text-[#C27803]" title={hasSubmitted ? 'Locked: Share to unlock' : 'Locked: Complete to unlock'}>
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Banner preview - Blur if user hasn't submitted OR hasn't shared publicly */}
                  <div className="relative mb-4">
                    <div
                      className={`relative w-full overflow-hidden rounded-2xl border border-[#F5D99B]/90 bg-[#FFF9EF] ring-1 ring-white/80 transition-[box-shadow] duration-300 ease-out group-hover:shadow-[0_0_0_1px_rgba(245,158,11,0.25),0_0_28px_rgba(251,191,36,0.18)] ${!hasSubmitted || !isUnlocked ? 'blur-sm' : ''} h-40 sm:h-44`}
                    >
                      {bannerUrl ? (
                        <img src={getFullImageUrl(bannerUrl)} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="image-fallback flex h-full w-full items-center justify-center">
                          <span className="text-sm text-[#8A6A44]">Gallery preview</span>
                        </div>
                      )}
                    </div>
                    <div className="absolute right-3 top-3 rounded-full border border-white/40 bg-[#2F251B]/75 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
                      {count} artwork{count !== 1 ? 's' : ''} shared
                    </div>
                    {(!hasSubmitted || !isUnlocked) && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex items-center gap-2 rounded-full bg-black/35 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-[2px]">
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {!hasSubmitted ? 'Locked: complete and share to unlock' : 'Locked: share to unlock'}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Button - Show only ONE button based on user's submission status */}
                  <div className="text-center">
                    {!hasSubmitted ? (
                      <button
                        type="button"
                        className="cursor-not-allowed rounded-full border border-[#E8E0D4] bg-[#F3EDE6] px-6 py-2.5 text-sm font-semibold text-[#8A6A44]"
                      >
                        Complete to unlock
                      </button>
                    ) : isUnlocked ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setWallModal(aid);
                        }}
                        className="rounded-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] px-7 py-2.5 text-sm font-semibold text-white shadow-[0_10px_26px_rgba(234,88,12,0.28)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_14px_32px_rgba(234,88,12,0.35)]"
                      >
                        Enter Gallery
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="rounded-full border border-[#F5D99B] bg-[#FFF3D6] px-6 py-2.5 text-sm font-semibold text-[#6B4E32]"
                      >
                        Share to unlock
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

          {visibleDailyActivities.length === 1 && (
            <aside className="mx-auto flex w-full max-w-md flex-col justify-center rounded-[28px] border border-[#F5D99B]/90 bg-gradient-to-b from-[#FFFDF9] to-[#FFF7ED] p-6 text-center shadow-[0_12px_36px_rgba(120,72,20,0.08)] lg:mx-0 lg:max-w-none lg:text-left">
              <div className="mx-auto mb-4 h-20 w-24 rounded-t-lg border-2 border-[#E8C97A]/70 bg-gradient-to-b from-white/80 to-amber-50/30 shadow-inner lg:mx-0" aria-hidden />
              <h3 className="text-lg font-semibold text-[#2F251B]">More artworks will appear here</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#6B4E32]">
                When young artists share their creations, this gallery will grow into a living museum.
              </p>
            </aside>
          )}
        </div>

        {visibleDailyActivities.length === 0 && !loading && hasLoadedOnce && (
          <div className="mx-auto mt-10 max-w-lg rounded-[28px] border border-[#F5D99B]/90 bg-gradient-to-b from-[#FFFDF9] to-[#FFF7ED] px-6 py-10 text-center shadow-[0_12px_36px_rgba(120,72,20,0.08)]">
            <div className="mx-auto mb-4 h-16 w-20 rounded-t-md border-2 border-[#E8C97A]/60 bg-white/50" aria-hidden />
            <h3 className="text-lg font-semibold text-[#2F251B]">The gallery is quiet for now</h3>
            <p className="mt-2 text-sm text-[#6B4E32]">
              {ageFilter !== 'all'
                ? 'No daily gallery shares from artists in this age range yet. Try another age group or All ages.'
                : 'When shared daily artworks appear, this wing will fill with new exhibits to explore together.'}
            </p>
          </div>
        )}
      </div>

      {/* Locked Activity Message Modal - Shows when user clicks on locked activity */}
      {showLockedMessage && (
        <CoLearningPromptBackdrop onClick={() => setShowLockedMessage(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="text-6xl mb-4">🔒</div>
              <h3 className={`mb-4 text-2xl font-bold ${coLearningInkTitle}`}>
                Activity Locked
              </h3>
              <p className={`mb-6 ${coLearningInkBody}`}>
                {(() => {
                  const aid = showLockedMessage;
                  const hasParticipated = userParticipations instanceof Set ? userParticipations.has(aid) : false;
                  const userSubs = userSubmissionsByActivity[aid] || [];
                  const hasSubmitted = hasParticipated || userSubs.length > 0;
                  
                  if (hasSubmitted) {
                    return "You need to share your artwork with the co-learning space to see what other artists have uploaded. Go to 'Art of the Day' and choose to share publicly to unlock this showcase!";
                  } else {
                    return "Submit your artwork for this activity through 'Art of the Day' to unlock and see what other artists have created! Complete the activity and share it publicly to access the co-learning space.";
                  }
                })()}
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => setShowLockedMessage(null)}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-full font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const targetId = showLockedMessage;
                    setShowLockedMessage(null);
                    navigate(
                      targetId
                        ? `/daily-art-activity?activityId=${encodeURIComponent(String(targetId))}`
                        : '/daily-art-activity'
                    );
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full font-semibold transition-colors"
                >
                  Go to this daily activity
                </button>
              </div>
            </div>
          </div>
        </CoLearningPromptBackdrop>
      )}

      {/* Legacy Locked Activity Modal - Keep for backward compatibility */}
      {selectedActivity && (!(userParticipations instanceof Set) || !userParticipations.has(selectedActivity.id)) && (
        <CoLearningPromptBackdrop>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🔒</div>
              <h3 className={`mb-4 text-2xl font-bold ${coLearningInkTitle}`}>
                Activity Locked
              </h3>
              <p className={`mb-6 ${coLearningInkBody}`}>
                Complete this activity in your daily activities to unlock the showcase and see what other artists have created!
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => setSelectedActivity(null)}
                  className="bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-full"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    const targetId = selectedActivity?.id;
                    setSelectedActivity(null);
                    navigate(
                      targetId
                        ? `/daily-art-activity?activityId=${encodeURIComponent(String(targetId))}`
                        : '/daily-art-activity'
                    );
                  }}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-3 px-6 rounded-full"
                >
                  Go to this daily activity
                </button>
              </div>
            </div>
          </div>
        </CoLearningPromptBackdrop>
      )}

      {/* Share-to-Unlock Prompt */}
      {sharePrompt && (
        <CoLearningPromptBackdrop>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🔓</div>
              <h3 className={`mb-4 text-2xl font-bold ${coLearningInkTitle}`}>Share to Unlock</h3>
              <p className={`mb-6 ${coLearningInkBody}`}>Share your artwork for this activity to unlock the Learning Space wall and see what other artists created.</p>
              <div className="flex gap-4 justify-center">
                <button onClick={() => setSharePrompt(null)} className="bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-full">Not now</button>
                <button onClick={confirmShareNow} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-3 px-6 rounded-full">Share my artwork</button>
              </div>
            </div>
          </div>
        </CoLearningPromptBackdrop>
      )}

      {/* Only show ActivityWallModal if user has submitted AND shared publicly */}
      {wallModal && (
        <ActivityWallModal activityId={wallModal} onClose={() => setWallModal(null)} />
      )}

      {/* Shared Artwork Modal */}
      {selectedSharedArtwork && (
        <CoLearningPromptBackdrop onClick={() => setSelectedSharedArtwork(null)}>
          <div
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-2xl font-bold ${coLearningInkTitle}`}>Your Shared Artwork</h3>
                <button
                  type="button"
                  onClick={() => setSelectedSharedArtwork(null)}
                  className="rounded-full px-2 text-2xl font-bold text-[#141210] hover:bg-stone-100"
                >
                  ×
                </button>
              </div>

              {/* Artwork Image */}
              <div className="mb-4">
                <div className="h-64 w-full overflow-hidden rounded-lg border-2 border-[#e8e0d4] bg-[#faf8f5]">
                  <img 
                    src={getFullImageUrl(selectedSharedArtwork.imageUrl || selectedSharedArtwork.userArtworkUrl)} 
                    alt="Your shared artwork" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.log('❌ Modal artwork image failed to load:', e.target.src);
                      e.target.style.display = 'none';
                      const fallback = e.target.parentElement.querySelector('.image-fallback');
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  {/* Fallback when image fails to load */}
                  <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                    <div className="text-center">
                      <div className="text-4xl mb-2">🎨</div>
                      <p className={`text-sm font-medium ${coLearningInkBody}`}>Your Artwork</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Artwork Details */}
              <div className="space-y-3">
                {(() => {
                  const activity = activities.find(a => a.id === selectedSharedArtwork.activityId);
                  const activityTitle = activity?.title || `Daily Activity ${selectedSharedArtwork.activityId}`;
                  
                  return (
                    <>
                      <div>
                        <h4 className={`text-lg font-semibold ${coLearningInkTitle}`}>
                          {activityTitle}
                        </h4>
                        {activity?.description && (
                          <p className={`mt-1 text-sm ${coLearningInkBody}`}>
                            {activity.description}
                          </p>
                        )}
                      </div>
                      
                      <div className={`flex items-center gap-4 text-sm ${coLearningInkBody}`}>
                        <div>
                          <span className="font-medium">Shared:</span> {selectedSharedArtwork.createdAt?.toDate?.() ? 
                            selectedSharedArtwork.createdAt.toDate().toLocaleDateString() : 
                            'Recently'
                          }
                        </div>
                        <div className="flex items-center gap-1 text-green-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span>Publicly shared</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Modal Actions */}
              <div className="flex gap-4 justify-center mt-6">
                <button
                  onClick={() => setSelectedSharedArtwork(null)}
                  className="bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-full hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setSelectedSharedArtwork(null);
                    // Navigate to the activity wall for this specific activity
                    setWallModal(selectedSharedArtwork.activityId);
                  }}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-3 px-6 rounded-full hover:from-amber-600 hover:to-orange-600 transition-colors"
                >
                  View Activity Wall
                </button>
              </div>
            </div>
          </div>
        </CoLearningPromptBackdrop>
      )}
    </div>
  );
};

export default DailyActivitiesShowcase;
